import { ReactNode, VFC } from 'react'
import styled from 'styled-components'
import { asModal, ModalProps } from 'src/components/hoc/asModal'
import { flexCenter } from 'src/styles/mixins'
import { orange, truewhite } from 'src/styles/colors'
import { fontWeightBold, fontWeightSemiBold } from 'src/styles/font'
import { REMAINING_PERIOD } from 'src/hooks/useFreeTrial'

type FreeTrialProps = {
  elapsedDays: number
  register: () => void
}

type FreeTrialBaseProps = {
  messageNode: ReactNode
  cancelLabel: string
  register: () => void
}

const FreeTrial: VFC<ModalProps<FreeTrialProps>> = ({
  elapsedDays,
  ...props
}) =>
  elapsedDays <= REMAINING_PERIOD.end ? (
    <FreeTrialRemain {...props} elapsedDays={elapsedDays} />
  ) : (
    <FreeTrialPrompt {...props} />
  )

const FreeTrialRemain: VFC<ModalProps<FreeTrialProps>> = ({
  elapsedDays,
  ...props
}) => (
  <FreeTrialBase
    {...props}
    messageNode={
      <>
        <h2>{`トライアル終了まで後${
          elapsedDays ? -elapsedDays : ''
        }日です`}</h2>
        <p>トライアルが終了すると下記利用制限がかかります。</p>
      </>
    }
    cancelLabel="トライアルを継続する"
  />
)

const FreeTrialPrompt: VFC<ModalProps<Omit<FreeTrialProps, 'elapsedDays'>>> = (
  props,
) => (
  <FreeTrialBase
    {...props}
    messageNode={
      <>
        <h2>トライアルが終了しました</h2>
        <p>
          無料トライアルが終了しました。
          <br />
          有料会員になることで下記機能が引き続きご利用いただけます。
        </p>
      </>
    }
    cancelLabel="キャンセル"
  />
)

const FreeTrialBase: VFC<ModalProps<FreeTrialBaseProps>> = ({
  close,
  register,
  messageNode,
  cancelLabel,
}) => {
  return (
    <Layout>
      {messageNode}
      <Functions>
        <div>掲示板機能の検索・キーワード登録機能</div>
        <div>決裁者の検索・ダイレクトメッセージ機能</div>
        <div>つながり対象が有料会員のみに制限</div>
        <div>オンリーストーリーからの企業紹介</div>
        <div>会員限定イベントの参加</div>
      </Functions>
      <ModalButtons>
        <CancelButton onClick={close}>{cancelLabel}</CancelButton>
        <RegisterButton onClick={register}>有料会員登録をする</RegisterButton>
      </ModalButtons>
    </Layout>
  )
}

export const FreeTrialModal = asModal(FreeTrial, { scrollable: true })

const Layout = styled.div`
  ${flexCenter};
  flex-direction: column;
  padding: 5%;
  width: 480px;
  max-width: min(80vw, 500px);
  border-radius: 12px;
  background-color: ${truewhite};
  line-height: 1.3rem;
  text-align: center;
  overflow-y: auto;

  h2 {
    padding-bottom: 10px;
    color: ${orange};
    font-size: 20px;
    font-weight: ${fontWeightBold};

    @media screen and (max-width: 540px) {
      font-size: 15px;
    }
  }

  p {
    margin-bottom: 20px;
    font-size: 12px;
    font-weight: ${fontWeightSemiBold};

    @media screen and (max-width: 540px) {
      font-size: 10px;
      line-height: 1rem;
    }
  }
`

const Functions = styled.div`
  width: 100%;
  color: ${orange};
  font-weight: ${fontWeightSemiBold};

  div {
    font-size: 12px;
    margin-bottom: 20px;
    padding: 10px 0;
    background-color: rgba(255, 108, 0, 0.2);
    border: solid 1px;
    border-radius: 20px;
    text-align: center;
  }
`

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;

  button {
    width: 48%;
    height: 40px;
    text-align: center;
    margin-bottom: 10px;
    padding: 6px 12px;
    border-radius: 5px;
    font-size: 14px;
    font-weight: ${fontWeightBold};
    line-height: 1.4;
    white-space: normal;

    @media screen and (max-width: 540px) {
      width: 80%;
      height: initial;
    }
  }

  @media screen and (max-width: 540px) {
    flex-direction: column;
    align-items: center;
  }
`

const RegisterButton = styled.button`
  color: ${truewhite};
  background: ${orange};
`

const CancelButton = styled.button`
  color: ${orange};
  border: solid 1px;
`
